import { SelMessage } from "@selector/shared/components";
import { groupBy } from "ramda";
import { type FC, type ReactNode } from "react";
import { Card, CardRowSimple, CardSection, ColoredLabel, type GridCard, HStack, InfoToggletip } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type NuFeature, type NuFeatureOpo, type Organism, type OrganismProperty } from "@/graphql.ts";
import { DEFAULT_ORG_PROPERTY_GROUP, ORG_PROPERTY_VALUE_TYPE, UNKNOWN_GENOME_NAME } from "@/settings/settings";

import style from "../../core/cards/styles/Card.module.scss";

interface IOrganismPropertiesCardProps extends GridCard {
  genome: Organism;
  title?: string;
}

interface INuFeatureOrganismPropertiesCardProps extends GridCard {
  feature: NuFeature;
}

const GenomeOrganismPropertiesCard: FC<IOrganismPropertiesCardProps> = (props) => {
  if (!props.genome || props.genome.name === UNKNOWN_GENOME_NAME) {
    return null;
  }

  let elements: ReactNode = <SelMessage id={"noContent.phenotypes"} />;

  // show the card only if we have motifs available
  if (props.genome.organismProperties?.length > 0) {
    elements = props.genome.organismProperties.map((p) => (
      <PropertyCardRow organismProperty={p} key={`card-row-org-property-${p?.organismPropertyType?.name}-${p?.id}`} />
    ));
  }

  return (
    <Card key="org-property-type-card" title={props.title ?? "Phenotypes"}>
      {elements}
    </Card>
  );
};

const NuFeatureOrganismPropertiesCard: FC<INuFeatureOrganismPropertiesCardProps> = (props) => {
  const nuFeature: NuFeature = props.feature;
  if (
    !props.feature ||
    props.feature.name === UNKNOWN_GENOME_NAME ||
    props.feature.nuFeatureOrganismProperties.length === 0
  ) {
    return null;
  }

  const groupedPropertiesBySource = groupBy(
    (featureOpo) => String(featureOpo!.source!.id),
    nuFeature.nuFeatureOrganismProperties!,
  );

  const getSourceTitle = (nuFeatureOpo: NuFeatureOpo) => {
    return `${nuFeatureOpo.source.sourceType.name}: ${nuFeatureOpo.source.organisation.name} ${
      nuFeatureOpo.source.owner ? `(${nuFeatureOpo.source.owner})` : ""
    }`;
  };

  return (
    <Card key="org-property-type-card" title={"Phenotypes"} shrinkable>
      {Object.keys(groupedPropertiesBySource).map((sourceId, index) => (
        <CardSection
          title={getSourceTitle(groupedPropertiesBySource[sourceId][index])}
          key={`org-property-type-card-${index}`}
        >
          {groupedPropertiesBySource[sourceId].map((nuFeatureOpo) => (
            <PropertyCardRow
              organismProperty={nuFeatureOpo?.organismProperty}
              key={`card-row-org-property-${nuFeatureOpo?.organismProperty?.organismPropertyType?.name}-${nuFeatureOpo?.id}`}
            />
          ))}
        </CardSection>
      ))}
    </Card>
  );
};

const PropertyCardRowTitle: FC<{ title: string; description: string }> = (props) => (
  <HStack>
    {props.title}
    {props.description && <InfoToggletip content={props.description} />}
  </HStack>
);

const PropertyCardRow: FC<{ organismProperty?: OrganismProperty }> = ({ organismProperty }) => {
  if (!organismProperty) return null;

  const type = organismProperty.organismPropertyType!;
  const group = type.group!;

  return (
    <CardRowSimple
      label={
        <HStack>
          {group.opgId !== DEFAULT_ORG_PROPERTY_GROUP && (
            <>
              <PropertyCardRowTitle title={group.name} description={group.description} />:{" "}
            </>
          )}
          <PropertyCardRowTitle title={type.name} description={type.description} />
        </HStack>
      }
    >
      {organismProperty?.organismPropertyType?.valueType === ORG_PROPERTY_VALUE_TYPE.NUMERIC ? (
        formatNumberByThousands(organismProperty?.numValue)
      ) : (
        <ColoredLabel color={organismProperty?.color}>{organismProperty?.value}</ColoredLabel>
      )}
      {organismProperty?.description && <div className={style.description}>{organismProperty.description}</div>}
    </CardRowSimple>
  );
};

export { GenomeOrganismPropertiesCard, NuFeatureOrganismPropertiesCard };
